import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MixpanelEvents } from '../../models/mixpanelEvents';
import { MixpanelService } from '../../services/mixpanel.service';
import { of, ReplaySubject, Subscription } from 'rxjs';
import { TableSettings } from '../../models/tableSettings';
import { catchError, switchMap } from 'rxjs/operators';
import { PlanogramCompletionService } from '../../services/planogram-completion.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { PlanogramCompletion } from '../../models/planogramCompletion';
import { ISearchSettings } from '../../../product/models/models';
import { Utils } from '../../services/utils.service';
import { ITableSettingColumn, ITableSettingColumnsData } from '../../models/tableSettingColumn';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ColumnsSettingsModalComponent } from '../../modals/columns-settings-modal/columns-settings-modal.component';
import { IUserTableSettings } from '../../models/userTableSettings';
import { SessionService } from '../../services/session.service';
import { ProductCardService } from '../../services/product-card.service';

import { SHARED_MODULES } from '../../../shared.';
import { LoaderComponent } from '../loader/loader.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhLoaderComponent } from '../ph-loader/ph-loader.component';
import { PhTableComponent } from '../ph-table/ph-table.component';
import { SearchComponent } from '../search/search.component';
import { ProductCardDrawerComponent } from '../product-card-drawer/product-card-drawer.component';

@Component({
    selector: 'app-planogram-completion[planogramId]',
    templateUrl: './planogram-completion.component.html',
    styleUrls: ['./planogram-completion.component.scss'],
	imports: [
		SHARED_MODULES,
		LoaderComponent,
		PhIconComponent,
		PhIconWrapperComponent,
		PhLoaderComponent,
		PhTableComponent,
		ProductCardDrawerComponent,
		SearchComponent
	],
    providers: [ProductCardService],
    standalone: true
})
export class PlanogramCompletionComponent implements OnInit, OnDestroy {

	private _subscriptions: Array<Subscription> = [];

    columnsData: ITableSettingColumnsData;
	data: PlanogramCompletion;
	loadData$: ReplaySubject<void> = new ReplaySubject<void>();
	initialSearchQuery: string;
	searchOptionsActive: boolean;
	searchSettings: Array<ISearchSettings>;
	showLoader: boolean = false;
	showTableLoader: boolean = false;
	tableSettings: TableSettings;

	@Input() planId: number;
	@Input() planogramId: number;
	@Input() storeId: number;

	constructor(
        private dialog: MatDialog,
		private mixPanelService: MixpanelService,
		private planogramCompletionService: PlanogramCompletionService,
        private sessionService: SessionService,
		private snackBarService: SnackBarService,
        private productCardService: ProductCardService
	) {}

	ngOnInit(): void {
		this.mixPanelService.trackPage(MixpanelEvents.PlanogramConfiguration);

		this.initialSearchQuery = this.planogramCompletionService.requestParams.getParam('search');
		this.searchSettings = this.planogramCompletionService.searchSettings;
		this.searchOptionsActive = this.planogramCompletionService.searchOptionsActive;

		this.tableSettings = new TableSettings({
			pagination: false,
			filters: false,
			search: false,
			columnsSettings: false,
			draggableColumns: false,
			excelDownload: false,
			firstColumnWidth: 200
		});

		if (
			this.planogramCompletionService.data &&
			this.planogramCompletionService.planogramId === this.planogramId &&
			this.planogramCompletionService.planId === this.planId
		) {
			this.data = this.planogramCompletionService.data;
            this.columnsData = this.planogramCompletionService.columns;
		} else {
			this.showLoader = true;
			this.loadData();

            this.planogramCompletionService.getColumns().subscribe({
                next: (response: ITableSettingColumnsData) => this.planogramCompletionService.columns = this.columnsData = response,
                error: () => this.snackBarService.setErrorNotification()
            });
		}

		this._subscriptions.push(
			this.loadData$
				.pipe(
					switchMap(() => this.planogramCompletionService
						.getData(
							this.planogramId,
							this.planId,
							this.planogramCompletionService.requestParams.getParams()
						)
						.pipe(
							catchError(() => {
								this.snackBarService.setErrorNotification();
								return of([]);
							})
						)
					)
				)
				.subscribe((response: PlanogramCompletion) => {
					this.showLoader = false;
					this.showTableLoader = false;
					if (!response) return;
					this.planogramCompletionService.data = this.data = response;
				}),
			this.productCardService.saveSubject.subscribe(() => this.loadData()),
			this.productCardService.saveProductsSubject.subscribe(() => this.loadData())
		);
	}

	loadData(): void {
		this.showTableLoader = true;
		this.planogramCompletionService.planogramId = this.planogramId;
		this.planogramCompletionService.planId = this.planId;
		this.loadData$.next();
	}

    openColumnsModal(): void {
        const dialogRef: MatDialogRef<ColumnsSettingsModalComponent> = this.dialog.open(ColumnsSettingsModalComponent, {
            width: '700px',
            autoFocus: false,
            backdropClass: 'back-drop',
            panelClass: 'scrollable-content',
            data: {
                allColumns: this.columnsData,
                visibleColumns: this.data.metrics
            }
        });

        dialogRef.afterClosed().subscribe((result: Array<string>) => {
            if (!result) return;
            const allColumns: Array<string> = Utils.to1DArray(
                this.columnsData.columns.map((group: ITableSettingColumn) => group.children.map((item: ITableSettingColumn) => item.key))
            );
            const columns: Array<string> = [...result];
            columns.unshift(...this.data.metrics.filter((column: string) => !allColumns.includes(column)));

            this.planogramCompletionService.requestParams.updateParams({
                metrics: columns
            });

            const updateParams: IUserTableSettings = {
                columns: columns
            };
            this.sessionService.settings.planogramCompletionTable = updateParams;
            this.sessionService.updateSettings$().subscribe({
                error: () => this.snackBarService.setErrorNotification()
            });
            this.loadData();
        });
    }

	search(value?: string): void {
		let searchFields: Array<string> = this.searchSettings
			.filter((setting: ISearchSettings) => setting.selected)
			.map((setting: ISearchSettings) => setting.name);

		if (!this.planogramCompletionService.searchOptionsActive) searchFields = [];

		if (
			Utils.isNullOrUndefined(value) &&
			(this.planogramCompletionService.requestParams.getParam('search_fields') || []).join(',') === searchFields.join(',')
		) return;

		this.searchOptionsActive = this.planogramCompletionService.searchOptionsActive;

		this.planogramCompletionService.requestParams.updateParams({
			search: value ?? this.planogramCompletionService.requestParams.getParam('search'),
			search_fields: searchFields
		});
		let settings: Object = {};
		this.searchSettings.forEach((setting: ISearchSettings) => settings[setting.name] = setting.selected);
		localStorage.setItem('planogramCompletionSearchSettings', JSON.stringify(settings));

		this.loadData();
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
	}
}
